import { MessageTag } from '../net/messageTags';
import { RoomName } from '../net/enums';
import { encodeStatus } from '../net/codec/lobby';
import { encodeGetGame } from '../net/codec/blackjack';
import type { ReconnectResult } from '../net/codec/reconnect';
import type { TransportAvailabilityInfo, TransportCloseInfo } from '../net/transport/ITransport';
import { gameState } from '../state/GameState';
import { toPersianDigits } from '../util/persian';
import { BlackjackTableScene } from '../scenes/BlackjackTableScene';
import { HokmTableScene } from '../scenes/HokmTableScene';
import { BackgammonTableScene } from '../scenes/BackgammonTableScene';
import { ReadyScene } from '../scenes/ReadyScene';
import { LoginScene } from '../scenes/LoginScene';
import { RegisterScene } from '../scenes/RegisterScene';
import { ReconnectScene } from '../scenes/ReconnectScene';
import { ReconnectModal } from '../ui/ReconnectModal';
import { installTelegramLifecycleHandlers } from '../platform/telegram';
import type { AppContext, ReconnectHandle } from './AppContext';

const MODAL_MAX_ATTEMPTS = 3;
const RESUME_TIMEOUT_MS = 8_000;
const MAX_RETRY_DELAY_MS = 15_000;
const RETRY_JITTER_MS = 500;

/**
 * Owns the authenticated connection lifecycle. Every reconnect operation is bound to one transport
 * generation; a timed-out generation is closed before another attempt can begin.
 */
export class ReconnectController implements ReconnectHandle {
  private active = false;
  private inFlight = false;
  private attempt = 0;
  private operationId = 0;
  private surface: 'modal' | 'page' = 'page';
  private modal: ReconnectModal | null = null;
  private page: ReconnectScene | null = null;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private lastActivationAt = 0;

  constructor(
    private readonly ctx: AppContext,
    private readonly wsUrl: string,
  ) {
    this.ctx.client.onClose((info) => this.onClose(info));
    this.ctx.client.onAvailability((info) => this.onAvailability(info));
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') this.handleActivation();
    });
    window.addEventListener('online', () => this.handleActivation());
    installTelegramLifecycleHandlers({
      activated: () => this.handleActivation(),
      deactivated: () => undefined,
    });
  }

  private onClose(info: TransportCloseInfo): void {
    if (!info.current) return;
    if (info.code === 4001 || info.reason === 'session_replaced') {
      this.failToLogin('این نشست در پنجره دیگری باز شده است.');
      return;
    }
    if (info.local || gameState.get().reconnectToken === '') return;
    if (this.isAuthenticationScene()) return;
    gameState.update({ connected: false });
    if (this.active) {
      this.inFlight = false;
      this.scheduleRetry();
      return;
    }
    this.begin();
  }

  private onAvailability(info: TransportAvailabilityInfo): void {
    if (info.state === 'suspended') {
      if (this.active) {
        if (this.inFlight) this.operationId++;
        this.inFlight = false;
        this.setStatus('سرور بازی در حال راه‌اندازی مجدد است…');
        return;
      }
      if (gameState.get().reconnectToken === '' || this.isAuthenticationScene()) return;
      gameState.update({ connected: false });
      this.begin();
      return;
    }

    if (this.active) {
      this.inFlight = false;
      this.tryReconnect();
    } else if (this.isAuthenticationScene()) {
      this.ctx.client.loadLogin();
    }
  }

  private begin(): void {
    if (this.active) return;
    this.active = true;
    this.inFlight = false;
    this.attempt = 0;
    const scene = this.ctx.scenes.active;
    const inGame = scene instanceof BlackjackTableScene ||
      scene instanceof HokmTableScene ||
      scene instanceof BackgammonTableScene ||
      scene instanceof ReadyScene;
    if (inGame) {
      this.surface = 'modal';
      this.modal = new ReconnectModal(() => this.cancelToLogin());
      this.ctx.uiLayer.addChild(this.modal);
    } else {
      this.surface = 'page';
      this.ctx.navigate(RoomName.Connect);
      this.page = this.ctx.scenes.active instanceof ReconnectScene ? this.ctx.scenes.active : null;
    }
    this.tryReconnect();
  }

  private tryReconnect(): void {
    if (!this.active || this.inFlight) return;
    this.clearTimer();
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      this.setStatus('اتصال اینترنت در دسترس نیست…');
      return;
    }
    // An open v2 socket waits for UPSTREAM_READY. A closed socket must be allowed into runAttempt so
    // it can create the replacement connection; requiring readiness before connect deadlocks recovery.
    if (this.ctx.client.isOpen && !this.ctx.client.isReady) {
      this.inFlight = false;
      this.setStatus('سرور بازی در حال راه‌اندازی مجدد است…');
      return;
    }
    this.inFlight = true;
    this.attempt++;
    const operationId = ++this.operationId;
    this.setStatus('در حال اتصال مجدد…');
    void this.runAttempt(operationId);
  }

  private async runAttempt(operationId: number): Promise<void> {
    const token = gameState.get().reconnectToken;
    if (!token) {
      this.inFlight = false;
      this.cancelToLogin();
      return;
    }
    try {
      if (!this.ctx.client.isOpen) await this.ctx.client.connect(this.wsUrl);
      if (!this.isCurrentOperation(operationId)) return;
      if (!this.ctx.client.isReady) {
        this.inFlight = false;
        this.setStatus('سرور بازی در حال راه‌اندازی مجدد است…');
        return;
      }
      const result = await this.ctx.client.reconnect(token);
      if (!this.isCurrentOperation(operationId)) return;
      if (!result.success) {
        if (result.errorMessage?.includes('دستگاه دیگری')) {
          this.ctx.client.faultConnection(4012, 'previous_session_releasing');
          throw new Error('Previous session is still releasing');
        }
        this.failToLogin(result.errorMessage ?? 'اتصال مجدد ناموفق بود. لطفاً دوباره وارد شوید.');
        return;
      }
      await this.restoreAuthoritativeState(result, operationId);
    } catch {
      if (!this.isCurrentOperation(operationId)) return;
      if (this.ctx.client.isReady) this.ctx.client.faultConnection(4010, 'reconnect_attempt_failed');
      this.inFlight = false;
      this.scheduleRetry();
      return;
    }
    if (this.operationId === operationId) this.inFlight = false;
  }

  private async restoreAuthoritativeState(result: ReconnectResult, operationId: number): Promise<void> {
    const gameId = result.gameUniqueId;
    if (!result.playingGame || !gameId) {
      gameState.update({ connected: true });
      this.finish();
      this.ctx.navigate(RoomName.MainMenu);
      return;
    }

    const connectionId = this.ctx.client.connectionId;
    if (connectionId === null) throw new Error('Reconnect socket closed before game recovery');
    this.setStatus('در حال بازیابی بازی…');

    const gameInfo = this.ctx.client.waitFor(MessageTag.GameInfo, {
      connectionId,
      timeoutMs: RESUME_TIMEOUT_MS,
      timeoutMessage: 'GameInfo recovery timed out',
    });
    this.ctx.client.send(encodeStatus());
    await gameInfo;
    if (!this.isCurrentOperation(operationId)) return;

    const snapshot = this.ctx.client.waitFor(MessageTag.GetGame, {
      connectionId,
      timeoutMs: RESUME_TIMEOUT_MS,
      timeoutMessage: 'GetGame recovery timed out',
    });
    // The native client sends the game id for all game types. Current server handlers tolerate the
    // field for Hokm/Backgammon and use the same GetGame tag for the authoritative snapshot.
    this.ctx.client.send(encodeGetGame(gameId));
    await snapshot;
    if (!this.isCurrentOperation(operationId)) return;

    gameState.update({ connected: true });
    this.finish();
  }

  private scheduleRetry(): void {
    if (!this.active) return;
    if (this.surface === 'modal' && this.attempt >= MODAL_MAX_ATTEMPTS) {
      this.fallbackToPage();
      return;
    }
    const exponent = Math.max(0, this.attempt - 1);
    const delay = Math.min(1000 * 2 ** exponent, MAX_RETRY_DELAY_MS) + Math.floor(Math.random() * RETRY_JITTER_MS);
    this.countdown(delay, () => this.tryReconnect());
  }

  private fallbackToPage(): void {
    this.clearTimer();
    this.removeModal();
    this.surface = 'page';
    this.attempt = 0;
    this.ctx.navigate(RoomName.Connect);
    this.page = this.ctx.scenes.active instanceof ReconnectScene ? this.ctx.scenes.active : null;
    this.scheduleRetry();
  }

  private failToLogin(message: string): void {
    this.finish();
    this.ctx.client.close();
    gameState.update({ reconnectToken: '', connected: false });
    this.ctx.navigate(RoomName.Login);
    this.ctx.dialog({ message });
  }

  cancelToLogin(): void {
    this.finish();
    this.ctx.client.close();
    gameState.update({ reconnectToken: '', connected: false });
    this.ctx.navigate(RoomName.Login);
  }

  retryNow(): void {
    if (!this.active) return;
    if (this.inFlight) {
      this.operationId++;
      this.inFlight = false;
      this.ctx.client.faultConnection(4011, 'manual_retry');
    }
    this.tryReconnect();
  }

  handleActivation(): void {
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
    const now = Date.now();
    if (now - this.lastActivationAt < 250) return;
    this.lastActivationAt = now;
    if (typeof navigator !== 'undefined' && navigator.onLine === false) return;

    if (this.ctx.client.isOpen) {
      this.ctx.client.probe();
      return;
    }
    if (this.active) {
      this.inFlight = false;
      this.tryReconnect();
      return;
    }
    if (gameState.get().reconnectToken && !this.isAuthenticationScene()) {
      gameState.update({ connected: false });
      this.begin();
      return;
    }
    if (this.isAuthenticationScene() && this.ctx.client.url) {
      void this.ctx.client.connect(this.wsUrl).catch(() => undefined);
    }
  }

  private countdown(delayMs: number, done: () => void): void {
    this.clearTimer();
    const started = Date.now();
    const tick = (): void => {
      if (!this.active) return;
      if (typeof navigator !== 'undefined' && navigator.onLine === false) {
        this.setStatus('اتصال اینترنت در دسترس نیست…');
        this.timer = null;
        return;
      }
      const remainingMs = delayMs - (Date.now() - started);
      if (remainingMs <= 0) {
        this.timer = null;
        done();
        return;
      }
      this.setStatus(`تلاش مجدد تا ${toPersianDigits(Math.ceil(remainingMs / 1000))} ثانیه دیگر…`);
      this.timer = setTimeout(tick, Math.min(1000, remainingMs));
    };
    tick();
  }

  private isCurrentOperation(operationId: number): boolean {
    return this.active && this.operationId === operationId;
  }

  private isAuthenticationScene(): boolean {
    const scene = this.ctx.scenes.active;
    return scene instanceof LoginScene || scene instanceof RegisterScene;
  }

  private setStatus(text: string): void {
    if (this.surface === 'modal') this.modal?.setStatus(text);
    else this.page?.setStatus(text);
  }

  private clearTimer(): void {
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
  }

  private removeModal(): void {
    if (this.modal && !this.modal.destroyed) {
      this.modal.removeFromParent();
      this.modal.destroy({ children: true });
    }
    this.modal = null;
  }

  private finish(): void {
    this.active = false;
    this.inFlight = false;
    this.operationId++;
    this.clearTimer();
    this.removeModal();
    this.page = null;
  }
}
